<template>
  <div class="invalid-accesses">
    <h2>{{ $t("views.invalidaccesses.title") }}</h2>
    <p class="text-muted mb-3">{{ $t("views.invalidaccesses.description") }}</p>

    <b-card class="invalid-accesses-filter-card mb-3" body-class="py-2 px-2">
      <template #header>
        <span class="invalid-accesses-filter-card__title">{{ $t("views.invalidaccesses.filter.title") }}</span>
      </template>

      <b-form @submit.prevent="resetAndFetch">
        <b-row class="align-items-end filter-row">
          <b-col cols="12" md="3" class="mb-2 mb-md-0 pr-md-2">
            <b-form-group :label="$t('views.invalidaccesses.filter.from')" label-for="invalid-accesses-filter-from" class="mb-0">
              <b-form-datepicker
                id="invalid-accesses-filter-from"
                v-model="filters.from"
                size="sm"
                :start-weekday="$store.getters['gui/getDatetimeWeekStartsOn']"
                :hide-header="true"
                :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                :locale="$store.getters['gui/getLanguage']"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3" class="mb-2 mb-md-0 pr-md-2">
            <b-form-group :label="$t('views.invalidaccesses.filter.to')" label-for="invalid-accesses-filter-to" class="mb-0">
              <b-form-datepicker
                id="invalid-accesses-filter-to"
                v-model="filters.to"
                size="sm"
                :start-weekday="$store.getters['gui/getDatetimeWeekStartsOn']"
                :hide-header="true"
                :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                :locale="$store.getters['gui/getLanguage']"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4" class="mb-2 mb-md-0 pr-md-2">
            <b-form-group :label="$t('views.invalidaccesses.filter.search')" label-for="invalid-accesses-filter-search" class="mb-0">
              <b-form-input
                id="invalid-accesses-filter-search"
                v-model.trim="filters.searchText"
                size="sm"
                :placeholder="$t('views.invalidaccesses.filter.searchPlaceholder')"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="mb-0">
            <b-button type="submit" variant="primary" size="sm" :disabled="isLoading" block>
              <b-spinner v-if="isLoading" small class="mr-1" />
              {{ $t("views.invalidaccesses.filter.apply") }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <b-table :items="items" :fields="fields" striped hover show-empty>
      <template #cell(created)="data">
        {{ formatDate(data.value) }}
      </template>
    </b-table>

    <div class="mt-3 d-flex flex-wrap align-items-center">
      <b-button
        variant="outline-primary"
        size="sm"
        :disabled="isLoading || !hasMore"
        class="mr-3 mb-2"
        @click="loadMore"
      >
        {{ $t("views.invalidaccesses.loadmore") }}
      </b-button>
      <span v-if="isLoading" class="text-muted mb-2">{{ $t("views.invalidaccesses.loading") }}</span>
    </div>
  </div>
</template>

<script>
import http from "@/$plugins/http/core";
import { STOREMODULE_ENDPOINTMODULE_MAP, STOREMODULE_ENDPOINT_MAP } from "@/constants";

export default {
  name: "InvalidAccesses",
  data() {
    return {
      items: [],
      fields: [
        { key: "created", label: this.$t("views.invalidaccesses.table.label.created") },
        { key: "eventType", label: this.$t("views.invalidaccesses.table.label.eventType") },
        { key: "reasonCode", label: this.$t("views.invalidaccesses.table.label.reasonCode") },
        { key: "reasonMessage", label: this.$t("views.invalidaccesses.table.label.reasonMessage") },
        { key: "locationName", label: this.$t("views.invalidaccesses.table.label.locationName") },
        { key: "email", label: this.$t("views.invalidaccesses.table.label.email") },
        { key: "phone", label: this.$t("views.invalidaccesses.table.label.phone") },
        { key: "clientId", label: this.$t("views.invalidaccesses.table.label.clientId") }
      ],
      filters: {
        from: null,
        to: null,
        searchText: ""
      },
      page: 1,
      pageSize: 100,
      cursorCreated: null,
      hasMore: true,
      isLoading: false
    };
  },
  mounted() {
    this.initDefaultFilters();
    this.resetAndFetch();
  },
  methods: {
    initDefaultFilters() {
      const to = new Date();
      to.setHours(23, 59, 59, 999);

      const from = new Date(to);
      from.setDate(from.getDate() - 30);
      from.setHours(0, 0, 0, 0);

      this.filters.from = from;
      this.filters.to = to;
    },
    parseFilterDate(value) {
      if (value == null || value === "") return null;
      if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : new Date(value.getTime());
      }
      if (typeof value === "string") {
        const s = value.trim();
        const ymdOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
        if (ymdOnly) {
          return new Date(+ymdOnly[1], +ymdOnly[2] - 1, +ymdOnly[3]);
        }
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
      }
      return null;
    },
    toApiDateTime(date) {
      if (!date) return null;
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },
    buildFetchUrl() {
      const baseUrl = `/${STOREMODULE_ENDPOINTMODULE_MAP.customer}/api/${STOREMODULE_ENDPOINT_MAP.invalidaccessattempts}/paged`;
      const params = new URLSearchParams();
      params.append("page", this.page.toString());
      params.append("pageSize", this.pageSize.toString());

      const fromBase = this.parseFilterDate(this.filters.from);
      if (fromBase) {
        const from = new Date(fromBase.getTime());
        from.setHours(0, 0, 0, 0);
        params.append("fromDate", this.toApiDateTime(from));
      }

      const toBase = this.parseFilterDate(this.filters.to);
      if (toBase) {
        const to = new Date(toBase.getTime());
        to.setHours(23, 59, 59, 999);
        params.append("toDate", this.toApiDateTime(to));
      }

      if (this.filters.searchText) {
        params.append("searchText", this.filters.searchText);
      }
      if (this.cursorCreated) {
        params.append("cursorCreated", this.cursorCreated);
      }

      return `${baseUrl}?${params.toString()}`;
    },
    fetchPage() {
      this.isLoading = true;
      return http({
        method: "get",
        url: this.buildFetchUrl()
      })
        .then((response) => {
          if (response.data.success === true) {
            const newItems = response.data.result || [];
            if (this.page === 1) {
              this.items = newItems;
            } else {
              this.items = this.items.concat(newItems);
            }

            this.hasMore = newItems.length === this.pageSize;
            this.cursorCreated = newItems.length > 0 ? newItems[newItems.length - 1].created : null;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return new Intl.DateTimeFormat("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      }).format(date);
    },
    resetAndFetch() {
      this.items = [];
      this.page = 1;
      this.cursorCreated = null;
      this.hasMore = true;
      this.fetchPage();
    },
    loadMore() {
      if (this.isLoading || !this.hasMore) return;
      this.page += 1;
      this.fetchPage();
    }
  }
};
</script>

<style lang="scss" scoped>
.invalid-accesses {
  .invalid-accesses-filter-card {
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  .invalid-accesses-filter-card__title {
    font-size: 0.8125rem;
    font-weight: 600;
  }
}
</style>
